import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Heart,
  Shield,
  TrendingUp,
  Users,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Menu,
  X,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  Frown,
  AlertCircle,
  Star,
  ShoppingBag,
  MessageCircle,
  DollarSign,
  Sparkles,
  Target,
  Award,
  Lock,
  Database,
  ChevronLeft,
  ChevronRight as ChevronRightIcon
} from 'lucide-react';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const heroSlides = [
    {
      image: '/it6ro4ha_1.jpg',
      alt: 'VeriSeek Banner 1'
    },
    {
      image: '/bbzflwd6_2.jpg',
      alt: 'VeriSeek Banner 2'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const formData = new FormData(e.target);
      
      const response = await fetch('https://formbold.com/s/ozKaE', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully.'
        });
        // Reset form
        e.target.reset();
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setFormStatus({
          type: 'error',
          message: 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Network error. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80; // Fixed header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };

  const features = [
    {
      icon: <ShoppingBag className="w-12 h-12" />,
      title: 'Eliminate Fake Reviews',
      description: 'Stop fake reviews from derailing your decisions. Our advanced AI identifies and removes fabricated content, ensuring you only see authentic buyer experiences that truly matter.'
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: 'Accurate Score Recalibration',
      description: 'Get honest ratings that reflect reality. We remove fake influence and recalibrate scores based purely on verified reviews, delivering unbiased results you can trust.'
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: 'Key Characteristic Extraction',
      description: 'Our technology highlights every detail you care about like durability, value, and ease of use so you get the insights that matter for your unique needs.'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Tailored Personalization',
      description: 'Take control of your shopping experience. Set your own priorities and let our AI recalibrate results to match your unique goals, lifestyle, and buying preferences.'
    },
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: 'Smart Product Comparison',
      description: 'Compare your chosen product with alternatives from different sellers or brands, highlighting key feature and pricing differences to easily find the best deal with confidence.'
    },
    {
      icon: <MessageCircle className="w-12 h-12" />,
      title: 'In-Depth Sentiment Analysis',
      description: 'Gain deeper understanding with AI-powered sentiment analysis, breaking down reviewer emotions and tones to reveal the true vibe, helping you decide with emotional clarity.'
    }
  ];

  const problems = [
    {
      icon: <X className="w-10 h-10" />,
      text: 'Too many reviews are fake, paid for, or biased.'
    },
    {
      icon: <AlertTriangle className="w-10 h-10" />,
      text: "Reviews don't match needs, priorities & uncertainty."
    },
    {
      icon: <Frown className="w-10 h-10" />,
      text: 'Conflicting reviews create confusion instead of clarity.'
    },
    {
      icon: <AlertCircle className="w-10 h-10" />,
      text: 'Missing key details (like reliability or support).'
    },
    {
      icon: <Star className="w-10 h-10" />,
      text: 'Herd mentality / inflated ratings hide real drawbacks.'
    }
  ];

  const trustReasons = [
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: 'AI-Driven Authenticity',
      description: 'Every review undergoes rigorous AI screening to eliminate fakes, ensuring absolute clarity and honesty. Trust our advanced algorithms for genuine insights.'
    },
    {
      icon: <Lock className="w-12 h-12" />,
      title: 'Data Privacy First',
      description: 'We never sell or share personal data. VeriSeek is built with privacy-by-design principles so security is always protected.'
    },
    {
      icon: <Award className="w-12 h-12" />,
      title: 'External Validation',
      description: 'Proven algorithms and methodologies ensure reliable, accurate analysis you can depend on consistently.'
    },
    {
      icon: <Database className="w-12 h-12" />,
      title: 'Commitment to Real Accountability',
      description: 'As an emerging company, we welcome external audits, specialist opinions, and user contributions to maintain equitable and dependable operations from the start.'
    }
  ];

  const supportWays = [
    {
      icon: <Heart className="w-12 h-12" />,
      title: 'Pray',
      description: 'For wisdom, protection, clarity, and God\'s favor as we build a truth-centered platform.'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Advocate',
      description: 'Share the mission with your church, networks, and Faith Driven Entrepreneur community.'
    },
    {
      icon: <DollarSign className="w-12 h-12" />,
      title: 'Invest / Donate',
      description: 'Help fund AI development, ethical engineering, and VeriSeek\'s global launch.'
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Partner',
      description: 'Become an early user or organizational ambassador for transparent commerce.'
    }
  ];

  const faqs = [
    {
      question: 'Is VeriSeek only for Christians?',
      answer: 'No. It is faith-inspired but serves every shopper globally.'
    },
    {
      question: 'How will my donation/investment be used?',
      answer: 'AI development, model training, engineering, ethical oversight, and scaling.'
    },
    {
      question: 'Is this a nonprofit?',
      answer: 'No, VeriSeek is a faith-grounded tech venture building a truth-first marketplace solution.'
    },
    {
      question: 'Can churches or Christian organizations partner?',
      answer: 'Absolutely — partnerships are welcomed globally.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md z-50 border-b border-gray-200">
        <nav className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img
                src="https://www.veriseek.ai/assets/veriseek_logo-C7oV1lU5.svg"
                alt="VeriSeek Logo"
                className="h-8 sm:h-10"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              <button
                onClick={() => scrollToSection('home')}
                className="text-sm whitespace-nowrap text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
              >
                HOME
              </button>
              <button
                onClick={() => scrollToSection('mission')}
                className="text-sm whitespace-nowrap text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
              >
                MISSION
              </button>
              <button
                onClick={() => scrollToSection('why-veriseek')}
                className="text-sm whitespace-nowrap text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
              >
                WHY VERISEEK
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="text-sm whitespace-nowrap text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
              >
                FEATURES
              </button>
              <button
                onClick={() => scrollToSection('support')}
                className="text-sm whitespace-nowrap text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
              >
                PARTNER WITH US
              </button>
              <button
                onClick={() => scrollToSection('founders-heart')}
                className="text-sm whitespace-nowrap text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
              >
                VISION
              </button>
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Button
                onClick={() => scrollToSection('contact')}
                variant="outline"
                className="border-[#00A7BB] text-[#00A7BB] hover:bg-[#00A7BB] hover:text-white transition-all duration-300"
              >
                Join the Mission
              </Button>
              <Button
                onClick={() => window.open('https://buy.stripe.com/fZudR90GEb7h5dhdlDffy00', '_blank')}
                className="bg-[#00A7BB] hover:bg-[#1B4C5F] text-white transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Donate / Invest
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200">
              <div className="flex flex-col gap-4">
                <button
                  onClick={() => scrollToSection('home')}
                  className="text-left text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
                >
                  HOME
                </button>
                <button
                  onClick={() => scrollToSection('mission')}
                  className="text-left text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
                >
                  MISSION
                </button>
                <button
                  onClick={() => scrollToSection('why-veriseek')}
                  className="text-left text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
                >
                  WHY VERISEEK
                </button>
                <button
                  onClick={() => scrollToSection('features')}
                  className="text-left text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
                >
                  FEATURES
                </button>
                <button
                  onClick={() => scrollToSection('support')}
                  className="text-left text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
                >
                  PARTNER WITH US
                </button>
                <button
                  onClick={() => scrollToSection('founders-heart')}
                  className="text-left text-gray-700 hover:text-[#00A7BB] transition-colors duration-200 font-medium"
                >
                  VISION
                </button>
                <div className="flex flex-col gap-3 pt-4">
                  <Button
                    onClick={() => scrollToSection('contact')}
                    variant="outline"
                    className="border-[#00A7BB] text-[#00A7BB] hover:bg-[#00A7BB] hover:text-white w-full"
                  >
                    Join the Mission
                  </Button>
                  <Button
                    onClick={() => window.open('https://buy.stripe.com/fZudR90GEb7h5dhdlDffy00', '_blank')}
                    className="bg-[#00A7BB] hover:bg-[#1B4C5F] text-white w-full"
                  >
                    Donate / Invest
                  </Button>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Slider */}
      <section id="home" className="relative w-full pt-16 sm:pt-20">
        <div className="relative w-full mx-auto overflow-hidden bg-gray-100">
          <div className="relative w-full max-w-full" style={{ paddingBottom: '28.58%' }}>
            {/* Slides */}
            {heroSlides.map((slide, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <a
                  href="https://buy.stripe.com/fZudR90GEb7h5dhdlDffy00"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full cursor-pointer"
                >
                  <img
                    src={slide.image}
                    alt={slide.alt}
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    loading="eager"
                    decoding="async"
                  />
                </a>
              </div>
            ))}

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-2 sm:left-4 lg:left-6 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-2 sm:p-3 rounded-full transition-all duration-300"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 sm:right-4 lg:right-6 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm p-2 sm:p-3 rounded-full transition-all duration-300"
              aria-label="Next slide"
            >
              <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
            </button>

            {/* Dots Navigation */}
            <div className="absolute bottom-3 sm:bottom-4 lg:bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-1.5 sm:gap-2">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? 'bg-white w-5 sm:w-6 lg:w-8 h-2 sm:h-2.5 lg:h-3'
                      : 'bg-white/50 hover:bg-white/75 w-2 sm:w-2.5 lg:w-3 h-2 sm:h-2.5 lg:h-3'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Slide 1: Mission Section - Restoring Truth and Trust */}
      <section id="mission" className="py-12 sm:py-16 lg:py-20 bg-[#1B4C5F] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
              Restoring Truth and Trust
              <br />
              in the Marketplace
            </h2>
            <p className="text-lg sm:text-xl text-white/90 mb-6 sm:mb-8">
              VeriSeek.ai — from Quaveritas ("as truth") — realigns commerce with Christian principles
              of honesty, integrity, and respect for every person as made in God's image.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-12 mb-6 sm:mb-8">
            <p className="text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
              The modern marketplace is overflowing with fake reviews, manipulated ratings, and broken trust.
              VeriSeek confronts this with ethical, transparent AI built on timeless biblical values.
            </p>
            <div className="border-l-4 border-[#00A7BB] pl-4 sm:pl-6 mb-4 sm:mb-6">
              <p className="text-lg sm:text-xl font-semibold mb-2">We believe:</p>
              <p className="text-base sm:text-lg italic">
                Whatever your task, work heartily, as serving the Lord and not men, knowing that from the Lord you will receive the inheritance as your reward; you are serving the Lord Christ.
              </p>
              <p className="text-sm text-white/80 mt-2">— Colossians 3:23-24</p>
            </div>
            <p className="text-base sm:text-lg leading-relaxed">
              When buyers receive honesty and businesses operate with integrity, society flourishes.
            </p>
          </div>

          <div className="text-center">
            <Button
              onClick={() => window.open('https://buy.stripe.com/fZudR90GEb7h5dhdlDffy00', '_blank')}
              size="lg"
              className="bg-[#00A7BB] hover:bg-white hover:text-[#1B4C5F] text-white text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 rounded-full shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <Heart className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
              Stand With Us in This Mission
            </Button>
          </div>
        </div>
      </section>

      {/* Slide 2: Why VeriSeek Section - The Marketplace Is Broken */}
      <section id="why-veriseek" className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C4C5F] mb-4 sm:mb-6 px-4">
              The Marketplace Is Broken —
              <br className="hidden sm:block" />
              <span className="block sm:inline"> And People Are Paying the Price</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                <div className="flex items-start space-x-3 sm:space-x-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div className="text-[#00A7BB] flex-shrink-0 text-xl sm:text-2xl font-bold">
                    90%+
                  </div>
                  <p className="text-gray-700 text-sm sm:text-base lg:text-lg">of online shoppers rely on reviews to decide.</p>
                </div>
                <div className="flex items-start space-x-3 sm:space-x-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div className="text-[#00A7BB] flex-shrink-0">
                    <X className="w-8 h-8 sm:w-10 sm:h-10" />
                  </div>
                  <p className="text-gray-700 text-sm sm:text-base lg:text-lg">A huge portion of these reviews are fake, paid, or manipulated.</p>
                </div>
                <div className="flex items-start space-x-3 sm:space-x-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div className="text-[#00A7BB] flex-shrink-0">
                    <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10" />
                  </div>
                  <p className="text-gray-700 text-sm sm:text-base lg:text-lg">Good, honest companies are buried under manufactured ratings.</p>
                </div>
                <div className="flex items-start space-x-3 sm:space-x-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div className="text-[#00A7BB] flex-shrink-0">
                    <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10" />
                  </div>
                  <p className="text-gray-700 text-sm sm:text-base lg:text-lg">Bad actors rise by gaming the system.</p>
                </div>
                <div className="flex items-start space-x-3 sm:space-x-4 bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div className="text-[#00A7BB] flex-shrink-0">
                    <DollarSign className="w-8 h-8 sm:w-10 sm:h-10" />
                  </div>
                  <p className="text-gray-700 text-sm sm:text-base lg:text-lg">Consumers lose money on products that never deserved trust.</p>
                </div>
              </div>
              <p className="text-lg sm:text-xl text-[#1C4C5F] font-semibold italic text-center px-4">
                This isn't just bad business — it's a moral issue.
              </p>
            </div>
            <div className="relative order-1 md:order-2">
              <img
                src="https://images.unsplash.com/photo-1633613286991-611fe299c4be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzl8MHwxfHNlYXJjaHwxfHxmYWtlJTIwcmV2aWV3cyUyMHN0YXJzfGVufDB8fHx8MTczNjEyNzQ3OHww&ixlib=rb-4.1.0&q=85"
                alt="Star Ratings - Review System"
                className="rounded-lg shadow-2xl w-full h-auto object-cover max-w-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Slide 3: Features Section - PRECISION, PERSONALIZATION, PEACE OF MIND */}
      <section id="features" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C4C5F] mb-4 px-4">
              PRECISION, PERSONALIZATION,
              <br />
              PEACE OF MIND
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="border-2 border-gray-200 hover:border-[#1C4C5F] transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 bg-white"
              >
                <CardContent className="p-4 sm:p-6 lg:p-8">
                  <div className="text-[#1C4C5F] mb-4 sm:mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1C4C5F] mb-3 sm:mb-4" style={{ fontSize: 'clamp(1.125rem, 1rem + 1vw, 1.5rem)' }}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base" style={{ fontSize: 'clamp(0.875rem, 0.75rem + 0.5vw, 1rem)' }}>
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Slide 4: Support Section - Partner With Us */}
      <section id="support" className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1B4C5F] mb-4">
              Partner With Us,
            </h2>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#00A7BB] mb-4 sm:mb-6">
              Christian Leaders & Faith-Driven Entrepreneurs
            </h3>
            <p className="text-lg sm:text-xl text-gray-700 max-w-3xl mx-auto">
              Your support empowers ethical technology that brings honesty back to commerce.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-12">
            {supportWays.map((way, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#00A7BB] transition-all duration-300 hover:shadow-xl text-center">
                <CardContent className="p-6 sm:p-8">
                  <div className="text-[#00A7BB] mb-4 sm:mb-6 flex justify-center">
                    {way.icon}
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1B4C5F] mb-3 sm:mb-4">
                    {way.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                    {way.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button
              onClick={() => window.open('https://buy.stripe.com/fZudR90GEb7h5dhdlDffy00', '_blank')}
              size="lg"
              className="bg-[#00A7BB] hover:bg-[#1B4C5F] text-white text-lg sm:text-xl px-8 sm:px-12 py-5 sm:py-7 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105"
            >
              DONATE NOW
              <ChevronRight className="ml-2 w-5 h-5 sm:w-6 sm:h-6" />
            </Button>
          </div>
        </div>
      </section>

      {/* Closing Banner Section */}
      <section className="w-full bg-gray-50">
        <div className="w-full max-w-[2000px] mx-auto">
          <img
            src="/lp-banner-2.jpg"
            alt="VeriSeek Vision"
            className="w-full h-auto object-contain"
            loading="lazy"
            decoding="async"
          />
        </div>
      </section>

      {/* Founders' Heart Section */}
      <section id="founders-heart" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#1B4C5F] mb-6">
              A Vision Rooted in Faith,
              <br />
              Built for the World
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              VeriSeek is founded on the belief that technology should elevate truth — never distort it.
              We are building not just a product, but a movement for ethical, honest commerce.
            </p>
          </div>

          <div className="bg-[#1B4C5F] text-white rounded-2xl p-8 sm:p-12 shadow-2xl">
            <div className="border-l-4 border-[#00A7BB] pl-6">
              <p className="text-2xl font-semibold italic leading-relaxed">
                "When truth thrives, people flourish.
                <br />
                When honesty leads, trust grows.
                <br />
                VeriSeek is our small part in restoring what the world has lost."
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => window.open('https://buy.stripe.com/fZudR90GEb7h5dhdlDffy00', '_blank')}
              size="lg"
              className="bg-[#00A7BB] hover:bg-[#1B4C5F] text-white text-lg px-8 py-6 rounded-full shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <ChevronRight className="mr-2 w-5 h-5" />
              Join the Vision
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-[#1B4C5F] mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-2 border-gray-100 rounded-lg px-6 hover:border-[#00A7BB] transition-colors duration-300"
              >
                <AccordionTrigger className="text-lg font-semibold text-[#1B4C5F] hover:text-[#00A7BB] py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pb-6 text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="bg-[#1B4C5F] text-white rounded-2xl p-8 sm:p-12">
              <h2 className="text-4xl font-bold mb-6">Contact Information</h2>
              <p className="text-xl mb-8">Any questions? - We can Help!</p>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <Phone className="w-6 h-6 text-[#00A7BB]" />
                  <a href="tel:+18885715335" className="text-lg hover:text-[#00A7BB] transition-colors">
                    +1-888-571-5335
                  </a>
                </div>
                <div className="flex items-center space-x-4">
                  <Mail className="w-6 h-6 text-[#00A7BB]" />
                  <a href="mailto:Support@veriseek.ai" className="text-lg hover:text-[#00A7BB] transition-colors">
                    Support@veriseek.ai
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h3 className="text-3xl font-bold text-[#1B4C5F] mb-8">Send us a Message</h3>
              
              {formStatus.message && (
                <div className={`mb-6 p-4 rounded-lg ${
                  formStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {formStatus.message}
                </div>
              )}
              
              <form action="https://formbold.com/s/ozKaE" method="POST" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A7BB] focus:border-transparent transition-all duration-200"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A7BB] focus:border-transparent transition-all duration-200"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A7BB] focus:border-transparent transition-all duration-200"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A7BB] focus:border-transparent transition-all duration-200"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00A7BB] focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Tell us how you'd like to support VeriSeek..."
                  ></textarea>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#00A7BB] hover:bg-[#1B4C5F] text-white text-lg py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B4C5F] text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {/* Column 1 - VeriSeek */}
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">VeriSeek</h3>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <a href="#" className="text-sm sm:text-base text-white/80 hover:text-white transition-colors">
                    About
                  </a>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('mission')}
                    className="text-sm sm:text-base text-white/80 hover:text-white transition-colors"
                  >
                    Mission
                  </button>
                </li>
                <li>
                  <a href="#" className="text-sm sm:text-base text-white/80 hover:text-white transition-colors">
                    Team
                  </a>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="text-sm sm:text-base text-white/80 hover:text-white transition-colors"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Column 2 - Product */}
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Product</h3>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <button
                    onClick={() => scrollToSection('features')}
                    className="text-sm sm:text-base text-white/80 hover:text-white transition-colors"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <a href="#" className="text-sm sm:text-base text-white/80 hover:text-white transition-colors">
                    How It Works
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 3 - Support */}
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Support</h3>
              <ul className="space-y-1.5 sm:space-y-2">
                <li>
                  <button
                    onClick={() => scrollToSection('faq')}
                    className="text-sm sm:text-base text-white/80 hover:text-white transition-colors"
                  >
                    FAQs
                  </button>
                </li>
                <li>
                  <a href="#" className="text-sm sm:text-base text-white/80 hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="text-sm sm:text-base text-white/80 hover:text-white transition-colors">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 4 - Social */}
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Follow Us</h3>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <a
                  href="https://www.linkedin.com/company/veriseek/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5 sm:w-6 sm:h-6" />
                </a>
                <a
                  href="https://www.youtube.com/channel/UCP4DL-yOxpxp_mJPw0HKV9w"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                  aria-label="YouTube"
                >
                  <Youtube className="w-5 h-5 sm:w-6 sm:h-6" />
                </a>
                <a
                  href="https://www.instagram.com/veriseek.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5 sm:w-6 sm:h-6" />
                </a>
                <a
                  href="https://www.facebook.com/veriseek"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5 sm:w-6 sm:h-6" />
                </a>
                <a
                  href="https://www.tiktok.com/@veriseek.ai?is_from_webapp=1&sender_device=pc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/20 pt-6 sm:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm sm:text-base text-white/80 text-center sm:text-left">
                © 2025 VeriSeek.ai — Building Truth for the Marketplace.
              </p>
              <div className="flex items-center gap-4 text-sm">
                <a href="tel:+18885715335" className="text-white/80 hover:text-white transition-colors">
                  +1-888-571-5335
                </a>
                <span className="text-white/50">|</span>
                <a href="mailto:Support@veriseek.ai" className="text-white/80 hover:text-white transition-colors">
                  Support@veriseek.ai
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky Donate Button for Mobile */}
      <div className="fixed bottom-4 right-4 z-50 lg:hidden">
        <Button
          onClick={() => window.open('https://buy.stripe.com/fZudR90GEb7h5dhdlDffy00', '_blank')}
          className="bg-[#00A7BB] hover:bg-[#1B4C5F] text-white px-6 py-3 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
        >
          <Heart className="w-4 h-4" />
          <span className="font-semibold">Donate</span>
        </Button>
      </div>
    </div>
  );
};

export default LandingPage;